/**
 * Entscheidet, was zu sehen ist: Anmeldung oder Anwendung.
 *
 * **Die Anmeldung hat keine eigene Adresse, und das ist Absicht.** Wer eine
 * Adresse der Anwendung aufruft, ohne angemeldet zu sein, sieht die Anmeldung —
 * die Adresse bleibt dabei stehen, und nach dem Anmelden ist er dort, wo er
 * hinwollte. Eine eigene `/anmelden`-Adresse müsste sich dieses Ziel erst wieder
 * merken, um es anschließend wiederherzustellen.
 *
 * Wohin es innerhalb der Anwendung geht, steht in `lib/router.ts`.
 *
 * Drei Zustände, nicht zwei: Beim Seitenaufruf steht noch nicht fest, ob im
 * Cookie eine Sitzung liegt. Solange `restore()` läuft, ist der Zustand
 * `unknown` — und die App zeigt weder Anmeldung noch Anwendung. Ohne diesen
 * Zwischenschritt erschiene beim Neuladen für einen Augenblick die
 * Anmeldeseite, was aussieht, als wäre man hinausgeworfen worden.
 */
import { useEffect, useSyncExternalStore } from "react";
import { Wordmark } from "@hausmeter/ui";
import { restore, sessionState, subscribe } from "./lib/session.js";
import { useRoute } from "./lib/router.js";
import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from "./routes/recovery.js";
import { LegalDocumentPage } from "./routes/legal.js";
import { InvitationPage } from "./routes/invitation.js";
import { LoginPage } from "./routes/login.js";
import { DashboardPage } from "./routes/dashboard.js";

const INITIAL = { status: "unknown", user: null } as const;

export function App() {
  /**
   * `useSyncExternalStore` und kein `useState`: Die Sitzung ändert sich auch
   * außerhalb von React — der Timer frischt auf, ein `401` beendet sie. React
   * bekommt den Zustand damit aus der einen Quelle, die ihn wirklich kennt.
   */
  const session = useSyncExternalStore(subscribe, sessionState, () => INITIAL);
  const route = useRoute();

  useEffect(() => {
    // `restore()` prüft selbst, ob es schon gelaufen ist — StrictMode ruft den
    // Effekt in der Entwicklung zweimal auf.
    void restore();
  }, []);

  /**
   * **Vor der Sitzungsweiche** (HM-026): Wer sein Passwort vergessen hat, kommt
   * gerade nicht herein — eine Ansicht dafür hinter der Anmeldung wäre nicht zu
   * erreichen. Und der Link aus einer E-Mail wird oft in einem Browser
   * geöffnet, in dem niemand angemeldet ist.
   *
   * Auch vor `status === "unknown"`: Diese drei Ansichten warten auf keine
   * Sitzung, und die leere Fläche während `restore()` sähe an einem Link aus
   * einer E-Mail aus wie eine Seite, die nicht lädt.
   */
  if (route.kind === "forgot-password") return <ForgotPasswordPage />;
  if (route.kind === "reset-password") return <ResetPasswordPage token={route.token} />;
  if (route.kind === "verify-email") return <VerifyEmailPage token={route.token} />;
  /**
   * **Die Einladung eines Bewohners** (HM-081) — vor der Sitzungsweiche wie die
   * drei Adressen aus HM-026, aber **nicht** vor `unknown`: Ob jemand
   * angemeldet ist, entscheidet, ob die Seite „annehmen" oder „registrieren"
   * zeigt. Der kurze Moment ohne Fläche ist hier der Preis für die richtige
   * Frage.
   */
  if (route.kind === "invitation" && session.status !== "unknown") {
    return <InvitationPage token={route.token} user={session.user} />;
  }

  /**
   * **Und die drei Rechtsseiten ebenso** (HM-061, OQ-033) — aber nur, solange
   * niemand angemeldet ist.
   *
   * Ohne Sitzung sind sie zwingend erreichbar: Ein Impressum ist nach § 5 DDG
   * „ständig verfügbar" zu halten, die Datenschutzerklärung nach Art. 13 DSGVO
   * **vor** der Erhebung zu geben, und wer den Nutzungsbedingungen zustimmen
   * soll, muss sie vorher lesen können. Sie stehen deshalb hier — vor der
   * Sitzungsweiche und vor `status === "unknown"`: Die leere Fläche während
   * `restore()` sähe an einem Link aus einer E-Mail aus wie eine Seite, die
   * nicht lädt.
   *
   * **Mit Sitzung übernimmt der Rahmen** (HM-061, `ui_kits/dashboard/`). Ein
   * angemeldeter Nutzer, der auf „Impressum" klickt, soll nicht in einer
   * Ansicht landen, die aussieht, als hätte er die Anwendung verlassen; das
   * Kit legt die Texte als Bereich in das Dashboard. Der **Text** ist in beiden
   * Fällen derselbe — nur der Rahmen wechselt (`LegalDocumentScreen`).
   *
   * **Während `restore()` gilt die eigenständige Fassung**, weil sie nichts
   * voraussetzt. Wer angemeldet ist und eine Rechtsadresse **neu lädt**, sieht
   * dadurch für einen Augenblick die eigenständige Seite und dann den Rahmen.
   * Das ist bewusst so gewählt: Der umgekehrte Weg — warten, bis die Sitzung
   * feststeht — zeigte jedem nicht angemeldeten Besucher zuerst eine leere
   * Fläche, und das ist der häufigere Fall. Der Text springt dabei nicht, nur
   * der Rahmen um ihn herum.
   */
  if (route.kind === "legal" && !session.user) {
    return <LegalDocumentPage document={route.document} />;
  }

  if (session.status === "unknown") return <Starting />;
  if (!session.user) return <LoginPage />;

  return <DashboardPage user={session.user} />;
}

/**
 * Die Fläche während `restore()`. Bewusst ohne Ladebalken und ohne Text wie
 * „Wird geladen": In aller Regel ist sie nach wenigen Hundert Millisekunden
 * vorbei, und ein Hinweis, der aufblitzt, beunruhigt mehr als er erklärt.
 */
function Starting() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        opacity: 0.35,
      }}
    >
      <Wordmark size={22} />
    </div>
  );
}
